import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Routes, Route, Navigate } from "react-router-dom";
import { ScrollToTop } from "./components/ScrollToTop";
import { MobileBottomNav } from "./components/MobileBottomNav";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { LoadingSpinner } from "./components/LoadingSpinner";
import AdminLayout from "./components/AdminLayout";
import { ChristmasSnow } from "./components/ChristmasSnow";

// Lazy load all pages to reduce initial bundle size
const Index = lazy(() => import("./pages/Index"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const CustomerLogin = lazy(() => import("./pages/CustomerLogin"));
const Profile = lazy(() => import("./pages/Profile"));
const AdminPanel = lazy(() => import("./pages/admin/Index"));
const Products = lazy(() => import("./pages/admin/Products"));
const AdminCategories = lazy(() => import("./pages/admin/Categories"));
const Orders = lazy(() => import("./pages/admin/Orders"));
const OrdersV2 = lazy(() => import("./pages/admin/OrdersV2"));
const Users = lazy(() => import("./pages/admin/Users"));
const Coupons = lazy(() => import("./pages/admin/Coupons"));
const AdminSupport = lazy(() => import("./pages/admin/Support"));
const Competitions = lazy(() => import("./pages/admin/Competitions"));
const Advertisements = lazy(() => import("./pages/admin/Advertisements"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const CustomerOrders = lazy(() => import("./pages/CustomerOrders"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const KnownBugs = lazy(() => import("./pages/KnownBugs"));
const CustomerSupport = lazy(() => import("./pages/Support"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const Returns = lazy(() => import("./pages/Returns"));
const SizeGuide = lazy(() => import("./pages/SizeGuide"));
const TreatCalculator = lazy(() => import("./pages/TreatCalculator"));
const K9Bud = lazy(() => import("./pages/K9Bud"));
const HealthTracker = lazy(() => import("./pages/HealthTracker"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const GuestOrderLookup = lazy(() => import("./pages/GuestOrderLookup"));
const PetCareGuide = lazy(() => import("./pages/PetCareGuide"));
const NutritionPlans = lazy(() => import("./pages/NutritionPlans"));
const ContentManager = lazy(() => import("./pages/admin/ContentManager"));
const Search = lazy(() => import("./pages/Search"));
const Categories = lazy(() => import("./pages/Categories"));
const Shop = lazy(() => import("./pages/Shop"));
const Sales = lazy(() => import("./pages/Sales"));
const ChristmasDeals = lazy(() => import("./pages/ChristmasDeals"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const PromotionalSections = lazy(() => import("./pages/admin/PromotionalSections"));
const AdminPetCareGuide = lazy(() => import("./pages/admin/PetCareGuide"));
const SiteSettings = lazy(() => import("./pages/admin/SiteSettings"));
const WebhookMonitor = lazy(() => import("./pages/admin/WebhookMonitor"));
const OrderRecovery = lazy(() => import("./pages/admin/OrderRecovery"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogCategory = lazy(() => import("./pages/BlogCategory"));
const BlogAdmin = lazy(() => import("./pages/admin/Blog"));
const BlogEditor = lazy(() => import("./pages/admin/BlogEditor"));
const BlogCategories = lazy(() => import("./pages/admin/BlogCategories"));
const Emails = lazy(() => import("./pages/admin/Emails"));
const CustomToys = lazy(() => import("./pages/CustomToys"));

const App = () => {
  return (
    <ErrorBoundary>
      <TooltipProvider>
        <ScrollToTop />
        <div className="relative min-h-screen pb-16 md:pb-0">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<ComingSoon />} />
              <Route path="/home" element={<Index />} />
              <Route path="/coming-soon" element={<ComingSoon />} />
              <Route path="/search" element={<Search />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/sales" element={<Sales />} />
              <Route path="/christmas-deals" element={<ChristmasDeals />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/customer/login" element={<CustomerLogin />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/orders" element={<Navigate to="/admin/ordersv2" replace />} />
              <Route path="/category/:id" element={<CategoryPage />} />
              <Route path="/product/:id" element={<ProductPage />} />
              <Route path="/order-success" element={<OrderSuccess />} />
              <Route path="/guest-orders" element={<GuestOrderLookup />} />
              <Route path="/known-bugs" element={<KnownBugs />} />
              <Route path="/support" element={<CustomerSupport />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/returns" element={<Returns />} />
              <Route path="/size-guide" element={<SizeGuide />} />
              <Route path="/treat-calculator" element={<TreatCalculator />} />
              <Route path="/k9bud" element={<K9Bud />} />
              <Route path="/feeding-calculator" element={<Navigate to="/treat-calculator" replace />} />
              <Route path="/health-tracker" element={<HealthTracker />} />
              <Route path="/pet-care-guide" element={<PetCareGuide />} />
              <Route path="/nutrition-plans" element={<NutritionPlans />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/custom-toys" element={<CustomToys />} />
              <Route
                path="/admin"
                element={
                  <AdminLayout>
                    <AdminPanel />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/products"
                element={
                  <AdminLayout>
                    <Products />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/categories"
                element={
                  <AdminLayout>
                    <AdminCategories />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/promotional-sections"
                element={
                  <AdminLayout>
                    <PromotionalSections />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/orders"
                element={
                  <AdminLayout>
                    <Navigate to="/admin/ordersv2" replace />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/ordersv2"
                element={
                  <AdminLayout>
                    <OrdersV2 />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminLayout>
                    <Users />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/coupons"
                element={
                  <AdminLayout>
                    <Coupons />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/support"
                element={
                  <AdminLayout>
                    <AdminSupport />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/competitions"
                element={
                  <AdminLayout>
                    <Competitions />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/advertisements"
                element={
                  <AdminLayout>
                    <Advertisements />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/content-manager"
                element={
                  <AdminLayout>
                    <ContentManager />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/pet-care-guide"
                element={
                  <AdminLayout>
                    <AdminPetCareGuide />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/site-settings"
                element={
                  <AdminLayout>
                    <SiteSettings />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/webhook-monitor"
                element={
                  <AdminLayout>
                    <WebhookMonitor />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/order-recovery"
                element={
                  <AdminLayout>
                    <OrderRecovery />
                  </AdminLayout>
                }
              />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/page/:page" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/blog/category/:slug" element={<BlogCategory />} />
              <Route path="/blog/category/:slug/page/:page" element={<BlogCategory />} />
              <Route
                path="/admin/blog"
                element={
                  <AdminLayout>
                    <BlogAdmin />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/blog/new"
                element={
                  <AdminLayout>
                    <BlogEditor />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/blog/edit/:id"
                element={
                  <AdminLayout>
                    <BlogEditor />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/blog/categories"
                element={
                  <AdminLayout>
                    <BlogCategories />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/emails"
                element={
                  <AdminLayout>
                    <Emails />
                  </AdminLayout>
                }
              />
            </Routes>
          </Suspense>
        </div>
        <MobileBottomNav />
        <ChristmasSnow />
        <Sonner />
        <ReactQueryDevtools initialIsOpen={false} />
      </TooltipProvider>
    </ErrorBoundary>
  );
};

export default App;
